import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";

// Before this, a failed write only reached console.error: the admin saw the
// row simply not change and had no way to tell a permission error from a slow
// network. `notify` puts the outcome on screen, and `confirm` replaces the
// delete buttons that fired immediately with no way back.
const FeedbackContext = createContext(null);

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) throw new Error("useFeedback must be used inside <FeedbackProvider>");
  return context;
};

const emptyConfirm = { open: false, title: "", message: "", confirmLabel: "", destructive: false };

function FeedbackProvider({ children }) {
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [confirmState, setConfirmState] = useState(emptyConfirm);
  const resolverRef = useRef(null);

  const notify = useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const confirm = useCallback(
    ({ title, message, confirmLabel = "Confirm", destructive = false }) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setConfirmState({ open: true, title, message, confirmLabel, destructive });
      }),
    []
  );

  const settle = (answer) => {
    setConfirmState((state) => ({ ...state, open: false }));
    if (resolverRef.current) {
      resolverRef.current(answer);
      resolverRef.current = null;
    }
  };

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((state) => ({ ...state, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((state) => ({ ...state, open: false }))}
          sx={{ color: "#fff" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmState.open} onClose={() => settle(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmState.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.95rem" }}>{confirmState.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => settle(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => settle(true)}
            variant="contained"
            color={confirmState.destructive ? "error" : "primary"}
            sx={{ color: "#fff" }}
          >
            {confirmState.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
}

FeedbackProvider.propTypes = { children: PropTypes.node.isRequired };

export default FeedbackProvider;
